import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { diariesApi, nutritionApi } from '../api';

/* ===== Types ===== */
interface Diary {
  id: number;
  content: string;
  date: string;
  created_at: string;
  diary_type?: DiaryType;
  person_name?: string;
  images?: { id: number; url: string }[];
}

type DiaryType = 'grudge' | 'gratitude' | 'note' | 'mood' | 'fitness' | 'tech';

interface MealRecord {
  id: number;
  meal_type: string;
  food_name: string;
  icon: string;
  amount: number;
  calories: number;
  protein: number;
  fat: number;
  carbs: number;
}

interface DaySummary {
  diaries: Diary[];
  meals: MealRecord[];
  totalCalories: number;
  totalProtein: number;
  totalFat: number;
  totalCarbs: number;
  waterCups: number;
  mealCount: number;
}

/* ===== Constants ===== */
const TAG_MAP: Record<string, { label: string; icon: string; color: string }> = {
  note:      { label: '日常', icon: '📋', color: '#0984E3' },
  mood:      { label: '情绪', icon: '💭', color: '#A29BFE' },
  fitness:   { label: '健身', icon: '💪', color: '#10B981' },
  tech:      { label: '技术', icon: '💻', color: '#F59E0B' },
  grudge:    { label: '记仇', icon: '😤', color: '#FF6B6B' },
  gratitude: { label: '记恩', icon: '🙏', color: '#00B894' },
};

const MEAL_TYPE_MAP: Record<string, { label: string; icon: string; color: string }> = {
  breakfast: { label: '早餐', icon: '🌅', color: '#f59e0b' },
  lunch:     { label: '午餐', icon: '☀️', color: '#10b981' },
  dinner:    { label: '晚餐', icon: '🌙', color: '#6366f1' },
  snack:     { label: '加餐', icon: '🍪', color: '#ec4899' },
};

const MOOD_EMOJIS = ['😊','😄','🥰','😌','😎','🤗','😢','😭','😤','😡','😰','😱','😔','😩','🤔','😴','🤒','🥳','😍','🙄'];

/* ===== Helpers ===== */
const pad = (n: number) => n.toString().padStart(2, '0');
const toDateStr = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const extractMoodEmoji = (c: string): string | null => {
  for (const e of MOOD_EMOJIS) if (c.includes(e)) return e;
  return null;
};

const getDiaryIcon = (d: Diary): string => {
  if (d.diary_type === 'mood') return extractMoodEmoji(d.content) || '💭';
  return TAG_MAP[d.diary_type || 'note']?.icon || '📋';
};

/* ===== Component ===== */
interface DiaryCalendarProps {
  onDateClick?: (date: Date, diaries: Diary[]) => void;
}

const DiaryCalendar: React.FC<DiaryCalendarProps> = ({ onDateClick }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [diaries, setDiaries] = useState<Diary[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [dayDetail, setDayDetail] = useState<DaySummary | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => { loadDiaries(); }, []);

  const loadDiaries = async () => {
    try {
      setLoading(true);
      const res = await diariesApi.getAll();
      setDiaries(Array.isArray(res.data) ? res.data : []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const diaryMap = useMemo(() => {
    const map: Record<string, Diary[]> = {};
    diaries.forEach(d => {
      const key = toDateStr(new Date(d.date || d.created_at));
      (map[key] ||= []).push(d);
    });
    return map;
  }, [diaries]);

  const getDiariesForDate = useCallback((dateStr: string) => diaryMap[dateStr] || [], [diaryMap]);

  const loadDayDetail = useCallback(async (dateStr: string) => {
    setDetailLoading(true);
    try {
      const dayDiaries = getDiariesForDate(dateStr);
      const [summaryRes, mealsRes] = await Promise.allSettled([
        nutritionApi.getDailySummary(dateStr),
        nutritionApi.getMeals(dateStr),
      ]);
      const summary = summaryRes.status === 'fulfilled' ? (summaryRes.value as any).data?.summary : null;
      const grouped = mealsRes.status === 'fulfilled' ? (mealsRes.value as any).data?.grouped : {};

      const allMeals: MealRecord[] = [];
      if (grouped) {
        Object.values(grouped).forEach((g: any) => {
          if (g?.meals) allMeals.push(...g.meals);
        });
      }

      setDayDetail({
        diaries: dayDiaries,
        meals: allMeals,
        totalCalories: summary?.total_calories || 0,
        totalProtein: summary?.total_protein || 0,
        totalFat: summary?.total_fat || 0,
        totalCarbs: summary?.total_carbs || 0,
        waterCups: summary?.water_cups || 0,
        mealCount: summary?.meal_count || 0,
      });
    } catch {
      setDayDetail({ diaries: getDiariesForDate(dateStr), meals: [], totalCalories: 0, totalProtein: 0, totalFat: 0, totalCarbs: 0, waterCups: 0, mealCount: 0 });
    } finally {
      setDetailLoading(false);
    }
  }, [getDiariesForDate]);

  /* ===== Calendar grid ===== */
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();

  const calendarDays = useMemo(() => {
    const days: (number | null)[] = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let i = 1; i <= daysInMonth; i++) days.push(i);
    return days;
  }, [daysInMonth, firstDay]);

  const todayStr = toDateStr(new Date());

  const handleDateClick = (day: number) => {
    const dateStr = `${year}-${pad(month + 1)}-${pad(day)}`;
    if (selectedDate === dateStr) {
      setSelectedDate(null);
      setDayDetail(null);
    } else {
      setSelectedDate(dateStr);
      loadDayDetail(dateStr);
      onDateClick?.(new Date(year, month, day), getDiariesForDate(dateStr));
    }
  };

  const handlePrev = () => { setCurrentDate(new Date(year, month - 1, 1)); setSelectedDate(null); setDayDetail(null); };
  const handleNext = () => { setCurrentDate(new Date(year, month + 1, 1)); setSelectedDate(null); setDayDetail(null); };

  /* ===== Day indicators ===== */
  const getDayIndicators = (day: number) => {
    const dateStr = `${year}-${pad(month + 1)}-${pad(day)}`;
    const dayDiaries = getDiariesForDate(dateStr);
    const hasDiary = dayDiaries.length > 0;
    const types = new Set(dayDiaries.map(d => d.diary_type || 'note'));
    return { hasDiary, types, count: dayDiaries.length };
  };

  const weekDays = ['日', '一', '二', '三', '四', '五', '六'];

  if (loading) {
    return (
      <div className="dc-root">
        <div className="dc-loading"><div className="dc-spinner" /><span>加载中...</span></div>
        <style>{STYLES}</style>
      </div>
    );
  }

  return (
    <div className="dc-root">
      {/* Header */}
      <div className="dc-header">
        <button className="dc-nav" onClick={handlePrev}>‹</button>
        <h3 className="dc-title">{year}年{month + 1}月</h3>
        <button className="dc-nav" onClick={handleNext}>›</button>
      </div>

      {/* Weekday labels */}
      <div className="dc-weekdays">
        {weekDays.map(d => <div key={d} className="dc-wd">{d}</div>)}
      </div>

      {/* Grid */}
      <div className="dc-grid">
        {calendarDays.map((day, idx) => {
          if (day === null) return <div key={idx} className="dc-cell dc-empty" />;
          const dateStr = `${year}-${pad(month + 1)}-${pad(day)}`;
          const isToday = dateStr === todayStr;
          const isSel = dateStr === selectedDate;
          const { hasDiary, types, count } = getDayIndicators(day);

          return (
            <button
              key={idx}
              type="button"
              className={`dc-cell ${isToday ? 'dc-today' : ''} ${isSel ? 'dc-sel' : ''} ${hasDiary ? 'dc-has' : ''}`}
              onClick={() => handleDateClick(day)}
            >
              <span className="dc-day">{day}</span>
              {hasDiary && (
                <div className="dc-dots">
                  {Array.from(types).slice(0, 3).map(t => (
                    <span key={t} className="dc-dot" style={{ background: TAG_MAP[t]?.color || '#0984E3' }} />
                  ))}
                </div>
              )}
              {count > 0 && <span className="dc-count">{count}</span>}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="dc-legend">
        {['note', 'mood', 'fitness', 'tech'].map(k => (
          <div key={k} className="dc-leg-item">
            <span className="dc-leg-dot" style={{ background: TAG_MAP[k].color }} />
            <span>{TAG_MAP[k].icon} {TAG_MAP[k].label}</span>
          </div>
        ))}
      </div>

      {/* Day Detail Panel */}
      {selectedDate && (
        <div className="dc-detail">
          <div className="dc-detail-head">
            <h4 className="dc-detail-title">
              {(() => {
                const [sy, sm, sd] = selectedDate.split('-').map(Number);
                return new Date(sy, sm - 1, sd).toLocaleDateString('zh-CN', { month: 'long', day: 'numeric', weekday: 'short' });
              })()}
            </h4>
            <button className="dc-detail-close" onClick={() => { setSelectedDate(null); setDayDetail(null); }}>✕</button>
          </div>

          {detailLoading ? (
            <div className="dc-detail-loading"><div className="dc-spinner" /><span>加载中...</span></div>
          ) : dayDetail ? (
            <>
              {/* Quick stats row */}
              <div className="dc-stats-row">
                <div className="dc-stat-card">
                  <span className="dc-stat-icon">📋</span>
                  <span className="dc-stat-val">{dayDetail.diaries.length}</span>
                  <span className="dc-stat-label">笔记</span>
                </div>
                <div className="dc-stat-card">
                  <span className="dc-stat-icon">🔥</span>
                  <span className="dc-stat-val">{Math.round(dayDetail.totalCalories)}</span>
                  <span className="dc-stat-label">千卡</span>
                </div>
                <div className="dc-stat-card">
                  <span className="dc-stat-icon">🍽️</span>
                  <span className="dc-stat-val">{dayDetail.mealCount}</span>
                  <span className="dc-stat-label">餐次</span>
                </div>
                <div className="dc-stat-card">
                  <span className="dc-stat-icon">💧</span>
                  <span className="dc-stat-val">{dayDetail.waterCups}</span>
                  <span className="dc-stat-label">杯水</span>
                </div>
              </div>

              {/* Macros bar */}
              {dayDetail.totalCalories > 0 && (
                <div className="dc-macros">
                  <div className="dc-macro"><span className="dc-m-dot" style={{ background: '#3b82f6' }} />蛋白 {Math.round(dayDetail.totalProtein)}g</div>
                  <div className="dc-macro"><span className="dc-m-dot" style={{ background: '#f59e0b' }} />脂肪 {Math.round(dayDetail.totalFat)}g</div>
                  <div className="dc-macro"><span className="dc-m-dot" style={{ background: '#10b981' }} />碳水 {Math.round(dayDetail.totalCarbs)}g</div>
                </div>
              )}

              {/* Diary entries */}
              {dayDetail.diaries.length > 0 && (
                <div className="dc-section">
                  <h5 className="dc-sec-title">📝 笔记记录</h5>
                  <div className="dc-entries">
                    {dayDetail.diaries.map(diary => {
                      const tag = TAG_MAP[diary.diary_type || 'note'] || TAG_MAP.note;
                      return (
                        <div key={diary.id} className="dc-entry">
                          <div className="dc-entry-head">
                            <span className="dc-entry-badge" style={{ background: tag.color + '22', color: tag.color }}>
                              {getDiaryIcon(diary)} {tag.label}
                            </span>
                            <span className="dc-entry-time">
                              {new Date(diary.date || diary.created_at).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                          <div className="dc-entry-body">
                            {diary.content.length > 120 ? diary.content.slice(0, 120) + '...' : diary.content}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Meal entries */}
              {dayDetail.meals.length > 0 && (
                <div className="dc-section">
                  <h5 className="dc-sec-title">🍽️ 饮食记录</h5>
                  <div className="dc-meals">
                    {Object.entries(MEAL_TYPE_MAP).map(([key, mt]) => {
                      const meals = dayDetail.meals.filter(m => m.meal_type === key);
                      if (meals.length === 0) return null;
                      return (
                        <div key={key} className="dc-meal-group">
                          <div className="dc-meal-head">
                            <span>{mt.icon} {mt.label}</span>
                            <span className="dc-meal-cal">{Math.round(meals.reduce((s, m) => s + m.calories, 0))} kcal</span>
                          </div>
                          {meals.map(m => (
                            <div key={m.id} className="dc-meal-item">
                              <span className="dc-meal-icon">{m.icon}</span>
                              <span className="dc-meal-name">{m.food_name}</span>
                              <span className="dc-meal-amt">{m.amount}g · {Math.round(m.calories)}kcal</span>
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Water */}
              {dayDetail.waterCups > 0 && (
                <div className="dc-section">
                  <h5 className="dc-sec-title">💧 饮水记录</h5>
                  <div className="dc-water">
                    <div className="dc-water-cups">
                      {Array.from({ length: Math.min(dayDetail.waterCups, 12) }).map((_, i) => (
                        <span key={i} className="dc-water-cup">💧</span>
                      ))}
                      {dayDetail.waterCups > 12 && <span className="dc-water-more">+{dayDetail.waterCups - 12}</span>}
                    </div>
                    <div className="dc-water-total">约 {dayDetail.waterCups * 250} ml</div>
                  </div>
                </div>
              )}

              {/* Empty day */}
              {dayDetail.diaries.length === 0 && dayDetail.meals.length === 0 && dayDetail.waterCups === 0 && (
                <div className="dc-empty-day">
                  <span className="dc-empty-icon">📭</span>
                  <span>这一天还没有记录</span>
                </div>
              )}
            </>
          ) : null}
        </div>
      )}

      <style>{STYLES}</style>
    </div>
  );
};

/* ===== Styles ===== */
const STYLES = `
  .dc-root {
    background: rgba(10, 15, 36, 0.97);
    border-radius: 22px;
    padding: 18px;
    color: #e2e8f0;
    font-family: inherit;
    border: 1px solid rgba(148, 163, 184, 0.12);
  }

  /* Header */
  .dc-header {
    display: flex; align-items: center; justify-content: space-between;
    margin-bottom: 14px;
  }
  .dc-title {
    margin: 0; font-size: 17px; font-weight: 800;
    background: linear-gradient(90deg, #a78bfa, #e2e8f0);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
  }
  .dc-nav {
    width: 36px; height: 36px; border-radius: 10px;
    border: 1px solid rgba(148,163,184,0.2);
    background: rgba(30,41,59,0.7); color: rgba(226,232,240,0.9);
    font-size: 20px; cursor: pointer; display: flex; align-items: center; justify-content: center;
    transition: all 0.15s;
  }
  .dc-nav:active { transform: scale(0.92); }

  /* Weekdays */
  .dc-weekdays {
    display: grid; grid-template-columns: repeat(7, 1fr);
    margin-bottom: 6px;
  }
  .dc-wd {
    text-align: center; font-size: 11px; font-weight: 700;
    color: rgba(148,163,184,0.5); padding: 6px 0;
    letter-spacing: 0.05em;
  }

  /* Grid */
  .dc-grid {
    display: grid; grid-template-columns: repeat(7, 1fr); gap: 3px;
  }
  .dc-cell {
    aspect-ratio: 1;
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    border-radius: 12px; border: none;
    background: transparent; color: rgba(226,232,240,0.85);
    cursor: pointer; position: relative;
    transition: all 0.15s; font-family: inherit;
    padding: 2px;
    gap: 2px;
  }
  .dc-cell.dc-empty { cursor: default; }
  .dc-cell:not(.dc-empty):hover { background: rgba(30,41,59,0.7); }
  .dc-cell:not(.dc-empty):active { transform: scale(0.93); }
  .dc-cell.dc-today {
    background: rgba(139,92,246,0.15);
    border: 1px solid rgba(139,92,246,0.35);
  }
  .dc-cell.dc-today .dc-day { color: #a78bfa; font-weight: 800; }
  .dc-cell.dc-sel {
    background: linear-gradient(135deg, rgba(139,92,246,0.3), rgba(217,70,239,0.2));
    border: 1px solid rgba(139,92,246,0.5);
    box-shadow: 0 0 12px rgba(139,92,246,0.2);
  }
  .dc-cell.dc-sel .dc-day { color: #fff; font-weight: 800; }
  .dc-day { font-size: 13px; line-height: 1; font-weight: 600; }

  /* Dots & count */
  .dc-dots {
    display: flex; gap: 2px; align-items: center; justify-content: center;
  }
  .dc-dot {
    width: 4px; height: 4px; border-radius: 50%;
    flex-shrink: 0;
  }
  .dc-count {
    position: absolute; top: 2px; right: 3px;
    font-size: 8px; font-weight: 800;
    color: rgba(148,163,184,0.45);
    line-height: 1;
  }

  /* Legend */
  .dc-legend {
    display: flex; gap: 12px; justify-content: center; flex-wrap: wrap;
    padding: 10px 0 2px; margin-top: 10px;
    border-top: 1px solid rgba(148,163,184,0.08);
  }
  .dc-leg-item {
    display: flex; align-items: center; gap: 5px;
    font-size: 11px; color: rgba(148,163,184,0.6);
  }
  .dc-leg-dot {
    width: 6px; height: 6px; border-radius: 50%;
  }

  /* ===== Detail panel ===== */
  .dc-detail {
    margin-top: 14px;
    border-top: 1px solid rgba(148,163,184,0.1);
    padding-top: 14px;
    animation: dcSlideIn 220ms ease;
  }
  @keyframes dcSlideIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }

  .dc-detail-head {
    display: flex; align-items: center; justify-content: space-between;
    margin-bottom: 12px;
  }
  .dc-detail-title {
    margin: 0; font-size: 15px; font-weight: 800;
    color: rgba(226,232,240,0.95);
  }
  .dc-detail-close {
    width: 28px; height: 28px; border-radius: 8px;
    border: 1px solid rgba(148,163,184,0.2);
    background: rgba(30,41,59,0.7); color: rgba(148,163,184,0.8);
    cursor: pointer; font-size: 13px;
    display: flex; align-items: center; justify-content: center;
  }

  /* Stats row */
  .dc-stats-row {
    display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px;
    margin-bottom: 12px;
  }
  .dc-stat-card {
    display: flex; flex-direction: column; align-items: center; gap: 2px;
    padding: 10px 4px;
    background: rgba(30,41,59,0.5);
    border: 1px solid rgba(148,163,184,0.08);
    border-radius: 14px;
  }
  .dc-stat-icon { font-size: 18px; }
  .dc-stat-val { font-size: 18px; font-weight: 900; color: rgba(226,232,240,0.95); line-height: 1.1; }
  .dc-stat-label { font-size: 10px; font-weight: 600; color: rgba(148,163,184,0.5); }

  /* Macros */
  .dc-macros {
    display: flex; gap: 12px; justify-content: center;
    padding: 8px 0; margin-bottom: 10px;
    background: rgba(30,41,59,0.3);
    border-radius: 10px;
  }
  .dc-macro {
    display: flex; align-items: center; gap: 5px;
    font-size: 11px; font-weight: 600; color: rgba(226,232,240,0.7);
  }
  .dc-m-dot {
    width: 6px; height: 6px; border-radius: 50%;
  }

  /* Section */
  .dc-section { margin-bottom: 12px; }
  .dc-sec-title {
    margin: 0 0 8px 0; font-size: 13px; font-weight: 700;
    color: rgba(226,232,240,0.8);
    padding-bottom: 6px;
    border-bottom: 1px solid rgba(148,163,184,0.08);
  }

  /* Diary entries */
  .dc-entries { display: flex; flex-direction: column; gap: 8px; }
  .dc-entry {
    padding: 10px 12px;
    background: rgba(30,41,59,0.5);
    border: 1px solid rgba(148,163,184,0.08);
    border-radius: 12px;
  }
  .dc-entry-head {
    display: flex; align-items: center; justify-content: space-between;
    margin-bottom: 6px;
  }
  .dc-entry-badge {
    display: inline-flex; align-items: center; gap: 4px;
    padding: 3px 8px; border-radius: 6px;
    font-size: 11px; font-weight: 700;
  }
  .dc-entry-time {
    font-size: 11px; color: rgba(148,163,184,0.45);
    font-family: ui-monospace, monospace;
  }
  .dc-entry-body {
    font-size: 13px; line-height: 1.6;
    color: rgba(226,232,240,0.8);
    white-space: pre-wrap; word-break: break-word;
  }

  /* Meal entries */
  .dc-meals { display: flex; flex-direction: column; gap: 8px; }
  .dc-meal-group {
    background: rgba(30,41,59,0.4);
    border: 1px solid rgba(148,163,184,0.06);
    border-radius: 12px; overflow: hidden;
  }
  .dc-meal-head {
    display: flex; align-items: center; justify-content: space-between;
    padding: 8px 12px;
    font-size: 13px; font-weight: 700; color: rgba(226,232,240,0.85);
  }
  .dc-meal-cal {
    font-size: 11px; font-weight: 600; color: rgba(148,163,184,0.5);
  }
  .dc-meal-item {
    display: flex; align-items: center; gap: 8px;
    padding: 7px 12px;
    border-top: 1px solid rgba(148,163,184,0.06);
  }
  .dc-meal-icon { font-size: 16px; flex-shrink: 0; }
  .dc-meal-name {
    flex: 1; font-size: 12px; font-weight: 600; color: rgba(226,232,240,0.8);
    min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .dc-meal-amt {
    font-size: 10px; color: rgba(148,163,184,0.45); white-space: nowrap;
  }

  /* Water */
  .dc-water {
    display: flex; align-items: center; justify-content: space-between;
    padding: 10px 12px;
    background: rgba(56,189,248,0.08);
    border: 1px solid rgba(56,189,248,0.15);
    border-radius: 12px;
  }
  .dc-water-cups { display: flex; gap: 3px; flex-wrap: wrap; }
  .dc-water-cup { font-size: 16px; }
  .dc-water-more { font-size: 11px; font-weight: 700; color: rgba(56,189,248,0.7); align-self: center; }
  .dc-water-total { font-size: 12px; font-weight: 700; color: rgba(56,189,248,0.7); white-space: nowrap; }

  /* Empty */
  .dc-empty-day {
    display: flex; flex-direction: column; align-items: center; gap: 8px;
    padding: 30px 20px;
    color: rgba(148,163,184,0.4); font-size: 13px;
  }
  .dc-empty-icon { font-size: 36px; }

  /* Loading */
  .dc-loading, .dc-detail-loading {
    display: flex; flex-direction: column; align-items: center; gap: 10px;
    padding: 30px 0; color: rgba(148,163,184,0.5); font-size: 13px;
  }
  .dc-spinner {
    width: 24px; height: 24px;
    border: 3px solid rgba(51,65,85,0.5); border-top-color: #a78bfa;
    border-radius: 50%; animation: dcSpin 0.8s linear infinite;
  }
  @keyframes dcSpin { to { transform: rotate(360deg); } }

  /* Mobile */
  @media (max-width: 420px) {
    .dc-root { padding: 14px; }
    .dc-day { font-size: 12px; }
    .dc-dot { width: 3px; height: 3px; }
    .dc-stats-row { grid-template-columns: repeat(2, 1fr); }
    .dc-stat-val { font-size: 16px; }
  }
`;

export default DiaryCalendar;
